import React, { useEffect, useMemo, useState } from "react";
import {
  Close as CloseIcon,
  DashboardOutlined,
  InsertChartOutlined,
  AccountCircleOutlined,
  MonetizationOnOutlined,
  Groups2Outlined,
  ListAltOutlined,
  Settings,
  MenuBookOutlined,
  SupervisedUserCircle,
  Login as LoginIcon,
  AccountBalanceWalletOutlined,
  SupportAgent,
  ImportExport,
  ArrowDropDownOutlined,
  ArrowDropUpOutlined,
} from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useDispatch, useSelector } from "react-redux";
import {
  closeAdminSidebar,
  toggleAdminSidebarCollapsed,
} from "../../app/adminSidebarSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "../../utils/logoImg";
import { Tooltip } from "@mui/material";
import { useBranding } from "../../context/BrandingContext";
import { DESIGN_TOKENS } from "../../designSystem/tokens";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { logoutMerchantUser } from "../../api/userAuth";
import { logout } from "../../features/authSlice";
import { showToastMessage } from "../../utils/toastUtils";

const HEADER_H = DESIGN_TOKENS.layout.navbarHeight;

const baseMenuItems = [
  { icon: <DashboardOutlined fontSize="medium" />, label: "Dashboard", path: "/admin/dashboard" },
  { icon: <InsertChartOutlined fontSize="medium" />, label: "Analytics", path: "/admin/analytics" },
  { icon: <AccountCircleOutlined fontSize="medium" />, label: "Profile", path: "/admin/profile" },
  // {
  //   icon: <Groups2Outlined fontSize="medium" />,
  //   label: "Pending Settlement",
  //   path: "/admin/settlement",
  // },
  {
    icon: <AccountBalanceWalletOutlined fontSize="medium" />,
    label: "Fund Transfer",
    path: "/admin/fund",
    superAdminOnly: true,
  },
  {
    icon: <AccountBalanceWalletOutlined fontSize="medium" />,
    label: "Wallets",
    path: "/admin/wallets",
  },

    {
    icon: <AccountBalanceWalletOutlined fontSize="medium" />,
    label: "Settlement Request",
    path: "/admin/settle-request",
  },
  {
    icon: <AccountBalanceWalletOutlined fontSize="medium" />,
    label: "Finkeda PG + Payout",
    children: [
      { label: "Dashboard", path: "/admin/finkeda" },
      { label: "Orders", path: "/admin/finkeda/orders" },
      { label: "Repair Orders", path: "/admin/finkeda/repair-orders" },
      { label: "PG Types", path: "/admin/finkeda/pg-types" },
      { label: "PG Commercials", path: "/admin/finkeda/pg-commercials" },
      { label: "Payout Pricing", path: "/admin/finkeda/payout-pricing" },
    ],
  },
  {
    icon: <MonetizationOnOutlined fontSize="medium" />,
    label: "Transactions",
    children: [
      // { label: "Add Money", path: "/admin/transaction/add-money" },
      // { label: "Utility", path: "/admin/transaction/utility" },
      // { label: "Settlement", path: "/admin/transaction/settlement" },
      // { label: "DMT", path: "/admin/transaction/dmt" },
      // { label: "AEPS", path: "/admin/transaction/aeps" },
      // { label: "Finkeda PG + Payout", path: "/admin/finkeda/orders" },
      { label: "CC BILL", path: "/admin/bill/ccbill" },
    ],
  },


  {
    icon: <MonetizationOnOutlined fontSize="medium" />,
    label: "Commission Rates",
    path: "/admin/commission-table",
  },
  {
    icon: <Groups2Outlined fontSize="medium" />,
    label: "Users",
    children: [
      { label: "All Users", path: "/admin/employee" },
      { label: "Pending KYC", path: "/admin/employee?accountStatus=PENDING_KYC" },
      { label: "Special Rates", path: "/admin/employee-table" },
    ],
  },
  {
    icon: <ListAltOutlined fontSize="medium" />,
    label: "Accounts",
    children: [
      { label: "Settlement Banks", path: "/admin/account" },
      { label: "Matm Banks", path: "/admin/matm-bank-list" },
      { label: "Bank Verifications", path: "/admin/bank-verifications" },
      { label: "Credit Card Billers", path: "/admin/credit-card-billers" },
      // { label: "Beneficiary Banks", path: "/admin/bene-accounts" },
      // { label: "Admin Banks", path: "/admin/bank" },
    ],
  },
  {
    icon: <SupervisedUserCircle fontSize="medium" />,
    label: "Services",
    path: "/admin/services",
  },
  {
    icon: <ImportExport fontSize="medium" />,
    label: "Reports / Export",
    path: "/admin/data-transfer",
  },
  // {
  //   icon: <MenuBookOutlined fontSize="medium" />,
  //   label: "System Guide",
  //   path: "/admin/system-guide",
  // },
  {
    icon: <SupportAgent fontSize="medium" />,
    label: "Enquiry",
    path: "/admin/enquiry-form",
  },
  {
    icon: <Settings fontSize="medium" />,
    label: "Settings",
    children: [
      { label: "Notice Board", path: "/admin/notice" },
      // { label: "Notifications", path: "/admin/notification" },
      // { label: "Support Info", path: "/admin/suports" },
      { label: "App Settings", path: "/admin/app-settings" },
      { label: "Settlement Charge", path: "/admin/settings/settlement-charge" },
      { label: "CreditCard Charge", path: "/admin/settings/credit-card-charge" },
      { label: "Provider Management", path: "/admin/providers" },
      // { label: "Credit Card Billers", path: "/admin/credit-card-billers" },
    ],
  },
  {
    icon: <LoginIcon fontSize="medium" />,
    label: "Merchant Portal",
    path: "__WEBSITE__",
    external: true,
  },
];

const SidebarAdmin = ({ mobile = false, collapsed: collapsedProp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const { collapsed: collapsedState } = useSelector((state) => state.adminSidebar);
  const collapsed = mobile ? false : collapsedProp ?? collapsedState;
  const { settings, companyName } = useBranding();
  const [portalConfirm, setPortalConfirm] = useState({ open: false, url: "" });

  const menuItems = useMemo(
    () =>
      baseMenuItems
        .filter((item) => !item.superAdminOnly || userInfo?.role === "super_admin")
        .map((item) =>
          item.path === "__WEBSITE__"
            ? {
                ...item,
                path: settings?.websiteUrl || "https://ezypayind.com",
                label: companyName ? `${companyName} Portal` : item.label,
              }
            : item
        ),
    [settings?.websiteUrl, companyName, userInfo?.role]
  );

  const pathMatches = (path) => {
    if (!path) return false;
    const [pathname, query = ""] = String(path).split("?");
    const onPath =
      location.pathname === pathname ||
      location.pathname.startsWith(`${pathname}/`);
    if (!onPath) return false;
    if (!query) {
      if (pathname === "/admin/employee" && location.pathname === "/admin/employee") {
        return !location.search.includes("accountStatus=");
      }
      if (pathname === "/admin/finkeda") {
        return location.pathname === "/admin/finkeda";
      }
      return true;
    }
    const want = new URLSearchParams(query);
    const have = new URLSearchParams(location.search);
    return [...want.entries()].every(([k, v]) => have.get(k) === v);
  };


  const [openDropdown, setOpenDropdown] = useState(null);
const [logoutOpen, setLogoutOpen] = useState(false);
const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    const activeParent = menuItems.find((item) =>
      item.children?.some((c) => pathMatches(c.path))
    );
    if (activeParent?.label) setOpenDropdown(activeParent.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const closeMobile = () => {
    if (mobile) dispatch(closeAdminSidebar());
  };

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  const renderNavItem = (item, index) => {
    const isDropdown = Boolean(item.children?.length);
    const childActive = item.children?.some((c) => pathMatches(c.path));
    const isActive = item.path ? pathMatches(item.path) : false;
    const isOpenDropdown =
      !collapsed && (openDropdown === item.label || !!childActive);
    const leafActive = isActive && !isDropdown;
    const navClass = `ds-nav-item ${leafActive ? "is-active" : ""} ${
      childActive || (isOpenDropdown && isDropdown) ? "is-parent-open" : ""
    } ${collapsed ? "is-collapsed" : ""}`;

    const wrapTip = (node, title) =>
      collapsed && !mobile ? (
        <Tooltip key={index} title={title} placement="right">
          <span className="block">{node}</span>
        </Tooltip>
      ) : (
        node
      );

    if (item.external) {
      return wrapTip(
        <button
          type="button"
          key={index}
          className={`${navClass} w-[calc(100%-16px)]`}
          onClick={() => setPortalConfirm({ open: true, url: item.path })}
        >
          <span className="ds-nav-icon">{item.icon}</span>
          {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
        </button>,
        item.label
      );
    }

    if (!isDropdown) {
      return wrapTip(
        <Link
          key={index}
          to={item.path}
          onClick={closeMobile}
          className={navClass}
        >
          <span className="ds-nav-icon">{item.icon}</span>
          {!collapsed && <span className="flex-1">{item.label}</span>}
        </Link>,
        item.label
      );
    }

    return (
      <div key={index}>
        {wrapTip(
          <button
            type="button"
            onClick={() => {
              if (collapsed) {
                dispatch(toggleAdminSidebarCollapsed());
                setOpenDropdown(item.label);
                return;
              }
              if (childActive) {
                setOpenDropdown(item.label);
                return;
              }
              toggleDropdown(item.label);
            }}
            className={`${navClass} w-[calc(100%-16px)]`}
          >
            <span className="ds-nav-icon">{item.icon}</span>
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isOpenDropdown ? (
                  <ArrowDropUpOutlined fontSize="small" />
                ) : (
                  <ArrowDropDownOutlined fontSize="small" />
                )}
              </>
            )}
          </button>,
          item.label
        )}
        {!collapsed && isOpenDropdown && (
          <div className="ml-3 pl-2 border-l border-[var(--border-color)] flex flex-col gap-0.5 mb-1">
            {item.children.map((child, i) => {
              const childIsActive = pathMatches(child.path);
              return (
                <Link
                  key={i}
                  to={child.path}
                  onClick={() => {
                    setOpenDropdown(item.label);
                    closeMobile();
                  }}
                  className={`ds-nav-child ${childIsActive ? "is-active" : ""}`}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };


  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutMerchantUser();
    } catch {
      /* still clear local session */
    } finally {
      dispatch(logout());
      setLoggingOut(false);
      setLogoutOpen(false);
      showToastMessage("success", "Logged out successfully");
      navigate("/", { replace: true });
    }
  };



  return (
    <div className="h-full flex flex-col bg-[var(--surface-color)] text-[var(--text-dark)]">
      <div
        className={`flex items-center flex-shrink-0 border-b border-[var(--border-color)] px-3 ${
          collapsed ? "justify-center" : "justify-between"
        }`}
        style={{ height: HEADER_H }}
      >
        {!collapsed && !mobile && <BrandLogo size="md" />}
        {collapsed && !mobile && <BrandLogo size="sm" />}
        {mobile && (
          <>
            <BrandLogo size="md" />
            <button
              type="button"
              className="p-1 rounded-[var(--radius-md)] border border-[var(--border-color)] ml-auto"
              onClick={closeMobile}
              aria-label="Close menu"
            >
              <CloseIcon fontSize="small" />
            </button>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="px-4 pt-3 pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            {companyName || "Control Panel"}
          </p>
          {userInfo?.name ? (
            <p className="text-[13px] font-semibold text-[var(--text-dark)] mt-0.5 truncate">
              {userInfo.name}
            </p>
          ) : null}
        </div>
      )}

      <ul
        className="flex-1 overflow-y-auto no-scrollbar py-2"
        style={{ scrollbarWidth: "none" }}
      >
        {menuItems.map((item, index) => renderNavItem(item, index))}
      </ul>

      <div className="flex-shrink-0 border-t border-[var(--border-color)] py-2 bg-[var(--surface-color)]">
        <Tooltip
          title={collapsed ? "Logout" : ""}
          placement="right"
          disableHoverListener={!collapsed}
        >
          <button
            type="button"
            className={`ds-nav-item ${collapsed ? "is-collapsed" : ""} w-[calc(100%-16px)] text-[var(--error-color)]`}
          onClick={() => {
  closeMobile();
  setLogoutOpen(true);
}}
          >
            <span className="flex text-[var(--error-color)]">
              <LogoutIcon fontSize="medium" />
            </span>
            {!collapsed && (
              <span className="text-[var(--error-color)]">Logout</span>
            )}
          </button>
        </Tooltip>
      </div>

   

        <ConfirmDialog
              open={logoutOpen}
              title="Sign out"
              message="Are you sure you want to logout from the admin panel?"
              confirmLabel="Logout"
              confirmColor="error"
              loading={loggingOut}
              onCancel={() => setLogoutOpen(false)}
              onConfirm={handleLogout}
            />
    </div>
  );
};

export default SidebarAdmin;
